import { Form, Formik, FormikProps } from "formik";
import Input from "../shared/Input/Input";
import Button from "@mui/material/Button";
import { useEffect, useMemo, useRef, useState } from "react";
import { errorMessages } from "../../constants/errorMessages";
import * as Yup from "yup";
import { regex } from "../../constants/regex";
import { Utility } from "../../utils/Utility";
import {
  useLazyForgotPasswordQuery,
  useLazyLoginQuery,
} from "../../services/rtkQueryServices/authenticationService";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loginCredentials } from "../../redux/slices/emailSlice";
import { ErrorMessage, LOGIN_LITERALS } from "../../constants/literals";
import { routePaths } from "../../constants/routePaths";
import { useAuth } from "../../context/AuthProvider";
import { ReactComponent as Alert } from "../../assets/svg/Alert.svg";
import { RegisteredUserMismatchErrorType } from "../../constants/enums/RegisteredUserMismatchErrorType";

export default function Login(props: any) {
  const [timer, setTimer] = useState("00:00");
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState<Boolean>();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState<string>("");
  const [isAccountLocked, setIsAccountLocked] = useState<boolean>();
  const initialFormValue = useMemo<any>(() => {
    return {
      email: "",
      password: "",
    };
  }, []);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [requestLogin] = useLazyLoginQuery();
  const [requestForgotPassword] = useLazyForgotPasswordQuery();
  const validationSchema = () =>
    Yup.object({
      email: Yup.string()
        .transform((value) => value.trim())
        .required(errorMessages.required("Email"))
        .matches(regex.emailValidation, () => errorMessages.validEmail),
      password: Yup.string().required(errorMessages.required("Password")),
    });
  const formRef = useRef<FormikProps<any>>(null);
  const auth = useAuth();
  const location = useLocation();
  const state = location.state;
  useEffect(() => {
    if (secondsLeft <= 0) {
      setIsAccountLocked(false);
      setLoginErrorMessage("");
    } else {
      const interval = setInterval(() => {
        if (secondsLeft > 0) {
          setSecondsLeft(secondsLeft - 1);
          let { totalSecs, hours, minutes, seconds } = Utility.getTimeRemaining(
            secondsLeft - 1
          );
          if (totalSecs >= 0) {
            setTimer(
              (minutes > 9 ? minutes : "0" + minutes) +
              ":" +
              (seconds > 9 ? seconds : "0" + seconds)
            );
          }
        }
        if (secondsLeft <= 0 && isAccountLocked) {
          setIsAccountLocked(false);
          setLoginErrorMessage("");
          setTimer("00:00");
        }
      }, 1000);
      return () => {
        clearInterval(interval);
      };
    }
  }, [secondsLeft]);
  const forgotPasswordRequest = (values: any) => {
    setForgotPasswordSuccess(false);
    setErrorMessage("");
    setLoginErrorMessage("");
    formRef?.current?.setTouched({ email: true });
    formRef.current?.values.email &&
      formRef.current?.values.email.includes("@") &&
      requestForgotPassword({
        userEmail: values?.trim(),
      })
        .then((res: any) => {
          if (res?.data?.isSuccess || res?.isError) {
            dispatch(loginCredentials(values));
            navigate(routePaths.otp, { state: { isloginValidation: false } });
          }
        })
        .catch((e: any) => {
          setErrorMessage(LOGIN_LITERALS.somethingWentWrong);
        });
  };

  const handleSubmit = (values: any) => {
    setForgotPasswordSuccess(false);
    let updatedValues = {
      ...values,
      email: values.email.trim(),
    };
    if (Utility.isUserTokenExpired(auth?.authClaims?.exp ?? 0)) {
      requestLogin(updatedValues)
        .then((res: any) => {
          let data: any = res?.data?.data;
          if (res?.data?.isSuccess) {
            if (data?.isLoginSuccess) {
              setLoginErrorMessage("");
              dispatch(loginCredentials(values.email));
              if (data.isLoginOtpEnabled) {
                navigate(routePaths.otp, {
                  state: { isloginValidation: true },
                });
              } else {
                auth?.logIn();
              }
            } else if (
              data?.isAccountLocked === false &&
              data?.attemptsLeft !== null
            ) {
              setLoginErrorMessage(
                ErrorMessage.ErrorInvalidUser + data?.attemptsLeft
              );
            } else if (data?.isAccountLocked) {
              setIsAccountLocked(true);
              setSecondsLeft(data?.timeLeft);
              setLoginErrorMessage(ErrorMessage.ErrorAccountLocked);
            } else {
              setErrorMessage((res.error as any).data.message);
            }
          } else {
            setErrorMessage((res.error as any).data.message);
          }
        })
        .catch((e) => {
          setErrorMessage(LOGIN_LITERALS.somethingWentWrong);
        });
    } else {
      setErrorMessage(errorMessages.activeSessionExists);
    }
  };
  const handleChange = () => {
    setErrorMessage("");
    setLoginErrorMessage("");
    setSecondsLeft(0);
  };

  return (
    <>
      {state?.isAccountExists &&
        (state?.matchType === RegisteredUserMismatchErrorType.EmailMismatch ||
          state?.matchType === RegisteredUserMismatchErrorType.CustomerIdMismatch) && (
          <>

            <div className="error-information p-2 my-3" style={{ fontSize: '2.0rem' }}>
              <span className="alert-icon"> <Alert fill="#d70825" style={{ transform: 'scale(1.5)' }} /></span>
              {LOGIN_LITERALS.somethingWentWrong}
            </div>
            <div className="error-information p-2 my-3" style={{ color: 'black' }}>
              {LOGIN_LITERALS.emailExists}<br />
              Please contact our support team at <strong>support@precedencehealth.com</strong> for assistance.
            </div>
          </>
        )}
      {(!state ||
        (state && !state?.isAccountExists) ||
        (state && state?.isAccountExists && state?.matchType === RegisteredUserMismatchErrorType.NoMismatch)) && (
          <div className="login-header d-flex flex-column align-items-center w-100 pt-5 pb-3">
            Sign in to your account
          </div>
        )}

      {(!state ||
        (state && !state?.isAccountExists) ||
        (state && state?.isAccountExists && state?.matchType === RegisteredUserMismatchErrorType.NoMismatch)) && (
          <>
            {state?.isAccountExists && state?.matchType === RegisteredUserMismatchErrorType.NoMismatch && (
              <div className="error-information p-2 my-3">
                {LOGIN_LITERALS.passwordAlreadySet}
              </div>
            )}
            <Formik
              initialValues={initialFormValue}
              validationSchema={validationSchema}
              onSubmit={(values: any) => {
                handleSubmit(values);
              }}
              innerRef={formRef}
            >
              {(formik) => (
                <Form
                  onChange={handleChange}
                  onSubmit={formik.handleSubmit}
                  className="login-form-container login-details"
                >
                  <div className="login-form d-flex flex-column">
                    <Input
                      //   onFocus={handleFocus}
                      label="Email Address"
                      name="email"
                      placeholder="Enter Email Address"
                      errorMsg={
                        formik.touched.email &&
                        formik.errors.email &&
                        (formik.errors as any).email
                      }
                      onInput={(e) => {
                        formik.handleChange(e);
                        setErrorMessage("");
                      }}
                      value={formik.values.email}
                      required
                    />
                    <Input
                      // onFocus={handleFocus}
                      label="Password"
                      name="password"
                      placeholder="Enter Password"
                      type="password"
                      onInput={(e) => {
                        formik.handleChange(e);
                        setErrorMessage("");
                      }}
                      errorMsg={
                        formik.touched.password &&
                        formik.errors.password &&
                        (formik.errors as any).password
                      }
                      value={formik.values.password}
                      maxLength={64}
                      required
                    // onClickShowHidePassword={() => {
                    //   setIsFocused(true);
                    // }}
                    />
                  </div>
                  <div className="login-actions">
                    <Button
                      className={
                        !formik.isValid || !formik.dirty || secondsLeft > 0
                          ? "form-button disabled"
                          : "form-button"
                      }
                      variant="contained"
                      type="submit"
                      disabled={!formik.isValid || !formik.dirty || secondsLeft > 0}
                    >
                      Sign In
                    </Button>
                  </div>
                  <div className="d-flex justify-content-end py-2">
                    <Button
                      className="link forgot-password"
                      onClick={() => {
                        forgotPasswordRequest(formik.values.email);
                      }}
                    >
                      Forgot Password?
                    </Button>
                  </div>
                  {errorMessage && (
                    <>
                      <div className="login-error-message p-4 mt-4">
                        <span className="error-message">{errorMessage} </span>
                      </div>
                    </>
                  )}
                  {loginErrorMessage && (
                    <>
                      <div className="login-error-message p-4 mt-4">
                        <span className="error-message">{loginErrorMessage} </span>
                        <span className="error-message">
                          {isAccountLocked && secondsLeft > 0 && timer}
                        </span>
                      </div>
                    </>
                  )}
                </Form>
              )}
            </Formik>
          </>
        )}
    </>
  );
}
